import React from "react";
import CodeBlock from "@/components/CodeBlock";

const FlagsDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        AxisNet CLI — Flags Documentation
      </h2>

      <p className="mb-6">
        AxisNet CLI uses a set of configuration flags that alter how generation,
        automation, Git workflows, and structural validation behave. Flags allow
        you to customize CLI behavior globally without passing additional
        parameters every time.
      </p>

      {/* FLAG TABLE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Available Flags (Summary Table)
        </h3>

        <CodeBlock
          language="bash"
          code={`╭────────────────────────────┬───────┬────────────────────────────────────────────────────────────────────────────────╮
│ Flag Name                  │ Value │ Description                                                                    │
├────────────────────────────┼───────┼────────────────────────────────────────────────────────────────────────────────┤
│ git-branch                 │ main  │ Specifies the active Git branch for commit/push operations.                    │
│ git-sync                   │  OFF  │ Automatically stages changes after each CLI operation.                         │
│ git-commit                 │  OFF  │ Automatically commits staged changes using a generated message.                │
│ git-push                   │  OFF  │ Automatically pushes commits to the remote Git repository.                     │
│ confirm-overwrite          │  OFF  │ Prompts for confirmation before overwriting existing files.                    │
│ autofix-dbcontext          │  OFF  │ Automatically inserts missing DbSet<T> into ApplicationDbContext.              │
│ use-controller-versioning  │  OFF  │ Enables automatic API versioning in controller generation.                     │
│ validate-project-structure │  OFF  │ Validates required project structure before execution.                         │
│ generate-docs              │  OFF  │ Creates README.md documentation for generated features.                        │
╰────────────────────────────┴───────┴────────────────────────────────────────────────────────────────────────────────╯`}
        />
      </div>

      {/* HOW TO SET FLAGS */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Manipulating Flags
        </h3>

        <p className="mb-4">
          Flags can be enabled or disabled at any time using the{" "}
          <code>flags set</code> command:
        </p>

        <CodeBlock
          language="bash"
          code={`axisnet flags set <flag-name> <on|off|true|false>`}
        />

        <p className="mt-4 mb-4">Examples:</p>

        <CodeBlock
          language="bash"
          code={`axisnet flags set git-sync on
axisnet flags set confirm-overwrite true
axisnet flags set autofix-dbcontext off
axisnet flags set use-controller-versioning on
axisnet flags set generate-docs true`}
        />
      </div>

      {/* FLAG DETAILS */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-6">
          Detailed Explanation of Each Flag
        </h3>

        {/* 1. git-branch */}
        <h4 className="text-xl font-semibold text-white mb-2">git-branch</h4>
        <p className="mb-4">
          Specifies which Git branch CLI operations (commit, push) will use.
          Default is <strong>main</strong>.
        </p>

        {/* 2. git-sync */}
        <h4 className="text-xl font-semibold text-white mb-2">git-sync</h4>
        <p className="mb-4">
          When enabled, the CLI automatically stages all created or modified
          files after each command execution.
        </p>

        {/* 3. git-commit */}
        <h4 className="text-xl font-semibold text-white mb-2">git-commit</h4>
        <p className="mb-4">
          Works together with <code>git-sync</code>. After staging, CLI
          automatically commits changes with a generated message.
        </p>

        {/* 4. git-push */}
        <h4 className="text-xl font-semibold text-white mb-2">git-push</h4>
        <p className="mb-4">
          When active, every commit created by the CLI is immediately pushed to
          the remote Git repository.
        </p>

        {/* 5. confirm-overwrite */}
        <h4 className="text-xl font-semibold text-white mb-2">
          confirm-overwrite
        </h4>
        <p className="mb-4">
          Prompts the user when a generator tries to overwrite an existing file.
          Recommended for production or shared repositories.
        </p>

        {/* 6. autofix-dbcontext */}
        <h4 className="text-xl font-semibold text-white mb-2">
          autofix-dbcontext
        </h4>
        <p className="mb-4">
          Automatically inserts missing <code>DbSet&lt;T&gt;</code> entries into{" "}
          <code>ApplicationDbContext</code> when generating domain entities.
        </p>

        {/* 7. use-controller-versioning */}
        <h4 className="text-xl font-semibold text-white mb-2">
          use-controller-versioning
        </h4>
        <p className="mb-4">
          Enables automatic API versioning. If no version is provided, the CLI
          inserts <strong>1.0</strong> as the default version.
        </p>

        {/* 8. validate-project-structure */}
        <h4 className="text-xl font-semibold text-white mb-2">
          validate-project-structure
        </h4>
        <p className="mb-4">
          Performs checks to confirm that required Application, Domain, and
          Infrastructure folders exist before generation starts.
        </p>

        {/* 9. generate-docs */}
        <h4 className="text-xl font-semibold text-white mb-2">generate-docs</h4>
        <p className="mb-2">
          When enabled, every feature created by the CLI includes a
          <strong> README.md</strong> file inside the Feature directory. This is
          ideal for documentation-oriented development workflows.
        </p>

        <CodeBlock
          language="bash"
          code={`/src/{{ProjectName}}.Application/Features/{{DomainName}}s/README.md`}
        />
      </div>
    </section>
  );
};

export default FlagsDoc;
